import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useT } from '@transifex/react';
import { RichTextRenderer } from '@raisin/components-lib';
import ArrowCircleRight from '@raisin/components-lib/Icons/ArrowCircleRight';
import { useAuthContext } from '@raisin/cp-layout';
import { getDistributor, isGBRLocale, useFeatureFlag } from '@raisin/cp-utils';
import { BANK_EVENTS, BANK_PAGES, trackBankDetailsPageClick } from 'eventsTracking';
import { useProductsAccess } from 'hooks';
import { DBSProduct } from 'types';
import { BankDetailsType } from 'types/bank';
import { getBankDetailPageLink, getProductsPageLink } from 'utils/bankPageUtils';
import { FormattedBankAdditionalDetails } from 'utils/bankPageUtils/bankAdditionalDetails';
import BankAdditionalDetails from '../BankAdditionalDetails';
import BankBestOffer from '../BankBestOffer';
import BankOffersList from '../BankOffersList/v1';
import messages from '../messages';
import DetailsSection from './DetailsSection';
import OffersDetailsSection from './OffersDetailsSection';
import * as S from './styles';

interface OffersSectionProps {
  bank: BankDetailsType;
  offers: DBSProduct[];
  selectedOfferId?: string;
  promotedProduct: DBSProduct | null;
  isTaxSectionAvailable: string | false | undefined;
  isProductPage?: boolean;
  bankAdditionalDetails?: FormattedBankAdditionalDetails;
}

const OffersSection: React.FC<OffersSectionProps> = ({
  bank,
  offers,
  selectedOfferId,
  promotedProduct,
  isTaxSectionAvailable,
  isProductPage,
  bankAdditionalDetails,
}) => {
  const t = useT();
  const { locale } = useIntl();
  const { isLoggedIn } = useAuthContext();
  const { distributorId } = useProductsAccess();
  const [expandedOffers, setExpandedOffers] = useState<Array<string>>([]);
  const [offerToOpen, setOfferToOpen] = useState<string | any>(null);
  const [scrollPending, setScrollPending] = useState<boolean>(false);
  const [isPromotedProductOpened, setIsPromotedProductOpened] = useState<boolean>(false);
  const onBankBestOfferClick = (offer: DBSProduct) => {
    setExpandedOffers([]);
    setOfferToOpen(offer.id);
    setScrollPending(true);
  };
  const { variation } = useFeatureFlag();
  const isProductLandingPageFeatureFlagOn = variation?.('b2cProductLandingPage', false);
  const shouldUseBankSlugFallback = variation?.('b2CShouldUseBankSlugFallback', false);
  const titleBasedOnTheFF = !isProductLandingPageFeatureFlagOn
    ? t(messages.offersSectionTitle)
    : t(messages.offersSectionTitleProductPage);

  useEffect(() => {
    if (scrollPending && !isPromotedProductOpened) {
      document.getElementById(offerToOpen)?.scrollIntoView({ behavior: 'smooth' });
      setScrollPending(false);
    }
  }, [scrollPending, offerToOpen, isPromotedProductOpened]);

  useEffect(() => {
    if (scrollPending || !offerToOpen) return;

    const timeout = setTimeout(() => {
      setExpandedOffers([offerToOpen]);
      setOfferToOpen(null);
      setIsPromotedProductOpened(true);
    }, 500);

    return () => clearTimeout(timeout);
  }, [scrollPending, offerToOpen]);

  const otherOffersLinkUrl = selectedOfferId
    ? getBankDetailPageLink(locale, shouldUseBankSlugFallback, bank.bank_slug, bank.name)
    : getProductsPageLink(locale, isLoggedIn);
  const otherOffersLinkText = selectedOfferId
    ? t(messages.showOtherOffers)
    : t(messages.showAllBankOffers);

  const otherOffersButtonLink = (
    <S.Button
      mode="primaryText"
      href={otherOffersLinkUrl}
      endIcon={<img src="/cp_assets/arrow_circle_right.svg" alt="arrow" />}
    >
      {otherOffersLinkText}
    </S.Button>
  );

  const offersWithoutSelectedOfferId = offers.filter(
    (offer) => !offer.id.includes(selectedOfferId as string),
  );

  const offersBasedOnProductPage = isProductPage ? offersWithoutSelectedOfferId : offers;

  return (
    <>
      {!selectedOfferId && promotedProduct && (
        <Box display={{ xs: 'block', lg: 'none' }}>
          <BankBestOffer offer={promotedProduct} onBankBestOfferClick={onBankBestOfferClick} />
        </Box>
      )}
      {offers?.length > 0 && (
        <OffersDetailsSection
          title={!selectedOfferId ? t(messages.offersSectionTitle) : titleBasedOnTheFF}
          subTitle={
            selectedOfferId && !isProductPage ? (
              <S.ButtonContainer item xs={12} lg={8} testId="offers-link-button">
                {otherOffersButtonLink}
              </S.ButtonContainer>
            ) : null
          }
          warning={
            (!selectedOfferId || isProductPage) && isGBRLocale(locale)
              ? t(messages.offersSectionWarning)
              : ''
          }
          promotedProduct={promotedProduct}
          selectedOfferId={selectedOfferId}
          headerItem={
            !selectedOfferId ? (
              <S.OfferListButton
                data-testid="show-all-offers"
                mode="primaryText"
                href={otherOffersLinkUrl}
                endIcon={<ArrowCircleRight />}
                onClick={() =>
                  trackBankDetailsPageClick({
                    distributorId: distributorId || getDistributor(),
                    eventName: BANK_EVENTS?.ALL_OFFERS_CTA_CLICKED,
                    action: 'clicked',
                    object: 'all_offers_cta',
                    bank_id: bank?.id,
                    productId: '',
                    product_type: '',
                    interestRates: {
                      interest_rate_nominal: '',
                      interest_rate_effective: '',
                    },
                    bankPage: BANK_PAGES.PRODUCT_PAGE,
                  })
                }
              >
                {otherOffersLinkText}
              </S.OfferListButton>
            ) : null
          }
          testId="offers-details-section"
        >
          {!selectedOfferId && (
            <S.ButtonContainer item xs={12} lg={8}>
              {otherOffersButtonLink}
            </S.ButtonContainer>
          )}
          <S.OfferListContainer item xs={12}>
            <Grid container>
              <Grid item xs={12} lg={promotedProduct && !selectedOfferId ? 8 : 12}>
                <BankOffersList
                  offers={offersBasedOnProductPage}
                  promotedProductId={promotedProduct?.id as string}
                  selectedOfferId={!isProductPage ? selectedOfferId : ''}
                  otherOffersLink={selectedOfferId ? otherOffersButtonLink : undefined}
                  expandedOffers={expandedOffers}
                  setExpandedOffers={setExpandedOffers}
                  setIsPromotedProductOpened={setIsPromotedProductOpened}
                />
              </Grid>
              {!selectedOfferId && promotedProduct && (
                <Grid item xs={12} lg={4} display={{ xs: 'none', lg: 'block' }}>
                  <BankBestOffer
                    testId="selected"
                    offer={promotedProduct}
                    onBankBestOfferClick={onBankBestOfferClick}
                  />
                </Grid>
              )}
            </Grid>
          </S.OfferListContainer>
        </OffersDetailsSection>
      )}
      <S.DetailsSectionContainer offersCount={offersBasedOnProductPage?.length || 0}>
        <DetailsSection
          title={t(messages.bankSectionDetailsTitle)}
          testId="bank-details-section"
          selectedOfferId={selectedOfferId as string}
        >
          <RichTextRenderer text={bank.description} />
        </DetailsSection>
        {bankAdditionalDetails && (
          <DetailsSection
            title={t(messages.bankSectionFurtherDetailsTitle)}
            testId="bank-additional-details-section"
            selectedOfferId={selectedOfferId as string}
          >
            <BankAdditionalDetails data={bankAdditionalDetails} />
          </DetailsSection>
        )}
        <DetailsSection
          title={t(messages.securityProtectionTitle)}
          testId="security-protection-section"
          selectedOfferId={selectedOfferId as string}
        >
          <div
            data-testid="anchor-tag"
            dangerouslySetInnerHTML={{
              __html: bank.statutory_deposit_guarantee_scheme.description,
            }}
          ></div>
        </DetailsSection>
        {isTaxSectionAvailable && (
          <DetailsSection
            title={t(messages.taxAndDocTitle)}
            testId="tax-documentation-section"
            selectedOfferId={selectedOfferId as string}
          >
            {bank.branch_country.source_tax_description!}
          </DetailsSection>
        )}
        <DetailsSection
          title={t(messages.raisinRoleTitle)}
          testId="raisin-role-section"
          selectedOfferId={selectedOfferId as string}
        >
          {t(messages.raisinRoleDetails)}
        </DetailsSection>
      </S.DetailsSectionContainer>
    </>
  );
};

export default OffersSection;
